import PropTypes from 'prop-types';
import React from 'react';

import SelectDropdown from '../../../../ComponentLibrary/input-elements/oc-select-dropdown';
import { connectLocation } from '../../../../location/LocationConnectors';
import { Title } from '../../styles';

// Section wrapper
export const Section = ({ title, children }) => (
  <fieldset style={{ border: 'none', padding: 16 }}>
    <Title>{title}</Title>
    <div style={{ display: 'grid', gap: 14, marginTop: 24 }}>{children}</div>
  </fieldset>
);
Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

// Generic vertical group of radios
export const RadioList = ({ name, value, onChange, options }) => (
  <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
    {options.map(opt => (
      // eslint-disable-next-line jsx-a11y/label-has-associated-control
      <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={String(value) === String(opt.value)}
          onChange={e => onChange(e.target.value)}
        />
        {opt.label}
      </label>
    ))}
  </div>
);

const RadioValueType = PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]);

RadioList.propTypes = {
  name: PropTypes.string.isRequired,
  value: RadioValueType, // instead of any
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: RadioValueType.isRequired, // instead of any
      label: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
};

RadioList.defaultProps = {
  value: null, // or '' if you prefer string-only values
};

// Yes/No radios
export const YesNo = connectLocation(({ name, value, onChange, yesLabel, noLabel, t }) => {
  // value can be: true | false | null/undefined
  const handleChange = e => {
    const v = e.target.value === 'yes';
    onChange(v);
  };

  const yesId = `${name}-yes`;
  const noId = `${name}-no`;

  return (
    <div role="radiogroup" aria-label={name} style={{ display: 'grid', gap: 8, marginTop: 8 }}>
      <label htmlFor={yesId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <input id={yesId} type="radio" name={name} value="yes" checked={value === true} onChange={handleChange} />
        {yesLabel || t('thermal_calculator.answer_yes')}
      </label>

      <label htmlFor={noId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input id={noId} type="radio" name={name} value="no" checked={value === false} onChange={handleChange} />
        {noLabel || t('thermal_calculator.answer_no')}
      </label>
    </div>
  );
});

YesNo.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  yesLabel: PropTypes.string,
  noLabel: PropTypes.string,
};

YesNo.defaultProps = {
  value: false,
  yesLabel: undefined,
  noLabel: undefined,
};

// Thin wrapper around SelectDropdown
export const DropdownField = connectLocation(({ id, name, value, onChange, options, disabled, style, t }) => {
  const opts =
    Array.isArray(options) && options.length > 0 && typeof options[0] === 'string'
      ? options
      : [{ value: '', text: t('thermal_calculator.select_option') }, ...options];

  return (
    <SelectDropdown
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      style={style}
      options={opts}
    />
  );
});

DropdownField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // instead of any
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  disabled: PropTypes.bool,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

DropdownField.defaultProps = {
  name: 'Select input',
  value: '',
  disabled: false,
  style: {},
};
